/**
 * CampaignMonitoringView — キャンペーンの効果測定モニタリング画面（P3）。
 *
 * 表示:
 *   - connections グラフをトポロジカル順序で layer 化し、左→右に Stats カードを配置
 *   - 同一 layer のカードは上下並列表示（並行ノード対応）
 *   - 各 Stats カードに reach 数 + 流入元別内訳
 *   - layer 間に CV 数 + CV 率を矢印として表示
 *
 * 注意:
 *   - LINE Broadcast 由来の reach は近似（個別 lineUid 不明）
 *   - Event カードは currentAttendees を手入力する UI を別途呼び出し
 *
 * 仕様書: P3 (campaign analytics monitoring)
 */
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Calculator,
  CalendarCheck,
  FileText,
  Link as LinkIcon,
  Mail,
  MessageCircle,
  MessagesSquare,
  Pencil,
  Smartphone,
  StickyNote,
  Users,
} from 'lucide-react';
import { useState, type ComponentType } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { db } from '@/lib/firebase';

import { EventAttendanceQuickEditDialog } from './EventAttendanceQuickEditDialog';

import type {
  Campaign,
  CampaignElement,
  CampaignElementType,
} from '@goenhub/shared';

import {
  buildCampaignLayers,
  useCampaignFunnel,
  type CardStats,
  type EdgeStats,
} from '@/hooks/useCampaignFunnel';

interface Props {
  campaignId: string;
  campaign: Campaign;
}

const ICON_BY_TYPE: Partial<Record<CampaignElementType, ComponentType<{ className?: string }>>> = {
  mail_send: Mail,
  mail_schedule: Mail,
  mail_draft: Mail,
  line_news: MessageCircle,
  form: FileText,
  landing: Smartphone,
  referral: LinkIcon,
  event: CalendarCheck,
  mail_contacts_group: Users,
  document_request: BookOpen,
  briefing_request: MessagesSquare,
  quote_request: Calculator,
  note: StickyNote,
};

const TYPE_LABEL: Record<CampaignElementType, string> = {
  mail_send: 'メール配信',
  mail_schedule: 'メール予約',
  mail_draft: 'メール',
  line_news: 'LINE',
  form: 'フォーム',
  landing: 'ランディング',
  referral: 'referral',
  event: 'イベント',
  mail_contacts_group: 'グループ',
  document_request: '資料請求',
  briefing_request: '個別説明',
  quote_request: '見積依頼',
  delay: '遅延',
  condition: '条件',
  metric: '指標',
  note: 'メモ',
};

// ─────────────────────────────────────────────
// LINE 配信数の補完（broadcast の sentRecipientCount を取得）
// ─────────────────────────────────────────────
function useLineNewsBroadcastCounts(campaignId: string | null | undefined) {
  return useQuery({
    queryKey: ['line_news_broadcast_counts', campaignId],
    enabled: !!campaignId,
    staleTime: 60_000,
    queryFn: async (): Promise<Map<string, number>> => {
      if (!campaignId) return new Map();
      const snap = await getDocs(
        query(
          collection(db, 'line_news_schedules'),
          where('campaignId', '==', campaignId),
        ),
      );
      const totals = new Map<string, number>();
      for (const d of snap.docs) {
        const data = d.data() as {
          newsId?: string;
          sentRecipientCount?: number | null;
          targetUserIds?: string[];
        };
        if (!data.newsId) continue;
        // broadcast 判定（targetUserIds.length === 0）も sentRecipientCount に依存
        if (typeof data.sentRecipientCount === 'number') {
          totals.set(
            data.newsId,
            (totals.get(data.newsId) ?? 0) + data.sentRecipientCount,
          );
        }
      }
      return totals;
    },
  });
}

// ─────────────────────────────────────────────
// 各カードの主要メトリクス（funnel hook の card.reach をそのまま採用）
// event カードは hook 内で currentAttendees が reach に格納されている
// ─────────────────────────────────────────────
function getPrimaryMetric(args: {
  el: CampaignElement;
  stats: CardStats | undefined;
  broadcastCount: number | null;
}): { value: number | string; label: string; note?: string } {
  const { el, stats, broadcastCount } = args;
  switch (el.type) {
    case 'mail_send':
    case 'mail_schedule':
    case 'mail_draft':
      return { value: stats?.reach ?? 0, label: '配信数' };
    case 'line_news': {
      const audCount = stats?.reach ?? 0;
      // multicast は audience_events に出るが broadcast は出ない。
      // sentRecipientCount を補助として表示。
      const total = audCount + (broadcastCount ?? 0);
      return {
        value: total,
        label: '配信数',
        note: broadcastCount && audCount === 0 ? '※Broadcast は近似' : undefined,
      };
    }
    case 'form':
      return { value: stats?.reach ?? 0, label: '申込数' };
    case 'landing':
      return { value: stats?.reach ?? 0, label: '閲覧数' };
    case 'referral':
      return {
        value: stats?.reach ?? 0,
        label: 'チャネル流入',
        note: 'タグ付き URL 経由',
      };
    case 'event':
      return {
        value: stats?.reach ?? 0,
        label: '参加実数',
        note: '手入力',
      };
    case 'document_request':
      return {
        value: stats?.reach ?? 0,
        label: '資料閲覧',
        note: 'CTA 経由',
      };
    case 'briefing_request':
      return {
        value: stats?.reach ?? 0,
        label: '説明申込',
        note: 'CTA 経由',
      };
    case 'quote_request':
      return {
        value: stats?.reach ?? 0,
        label: '見積申込',
        note: 'CTA 経由',
      };
    default:
      return { value: '—', label: TYPE_LABEL[el.type] ?? '' };
  }
}

// ─────────────────────────────────────────────
// 1 カード分の表示
// ─────────────────────────────────────────────
function StatsCard({
  el,
  stats,
  broadcastCount,
  onEditEvent,
}: {
  el: CampaignElement;
  stats: CardStats | undefined;
  broadcastCount: number | null;
  /** event カードのみ：編集ボタン押下時 */
  onEditEvent?: (el: CampaignElement) => void;
}) {
  const Icon = ICON_BY_TYPE[el.type] ?? StickyNote;
  const metric = getPrimaryMetric({ el, stats, broadcastCount });
  const breakdown = stats?.bySource;
  const isEvent = el.type === 'event';

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 min-w-[220px] max-w-[260px] flex-shrink-0 relative">
      {isEvent && el.refId && onEditEvent && (
        <button
          type="button"
          className="absolute top-2 right-2 p-1.5 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors"
          onClick={() => onEditEvent(el)}
          title="参加実数を編集"
        >
          <Pencil className="size-3.5" />
        </button>
      )}
      <div className="flex items-center gap-2 mb-2">
        <Icon className="size-4 text-[var(--text-muted)]" />
        <span className="text-[11px] uppercase tracking-wider text-[var(--text-muted)]">
          {TYPE_LABEL[el.type]}
        </span>
      </div>
      <div className="text-[13px] font-semibold truncate mb-3" title={el.refLabel ?? '(未紐付)'}>
        {el.refLabel || '(未紐付)'}
      </div>
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-2xl font-bold tabular-nums">
          {typeof metric.value === 'number'
            ? metric.value.toLocaleString('ja-JP')
            : metric.value}
        </span>
        <span className="text-xs text-[var(--text-muted)]">{metric.label}</span>
      </div>
      {metric.note && (
        <div className="text-[10px] text-[var(--text-muted)] flex items-center gap-1 mb-2">
          <AlertTriangle className="size-3" />
          {metric.note}
        </div>
      )}
      {breakdown && (breakdown.total ?? 0) > 0 && (
        <div className="text-[10px] text-[var(--text-muted)] space-y-0.5 pt-2 border-t border-[var(--border)]">
          {(['mail', 'line', 'landing', 'referral', 'direct'] as const).map(
            (src) => {
              const v = breakdown[src];
              if (!v) return null;
              const labels = {
                mail: 'メール経由',
                line: 'LINE 経由',
                landing: 'LP 経由',
                referral: 'タグ付き URL',
                direct: 'ダイレクト',
              };
              return (
                <div key={src} className="flex justify-between">
                  <span>{labels[src]}</span>
                  <span className="tabular-nums">{v}</span>
                </div>
              );
            },
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// 矢印 + CV ラベル
// ─────────────────────────────────────────────
function CvBadge({ edges }: { edges: EdgeStats[] }) {
  if (edges.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center mx-2 text-[var(--text-muted)]">
        <ArrowRight className="size-5" />
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center mx-3 gap-1 min-w-[80px]">
      {edges.map((e) => (
        <div
          key={`${e.fromCardId}-${e.toCardId}`}
          className="flex flex-col items-center text-center"
        >
          <ArrowRight className="size-4 text-[var(--text-muted)]" />
          <div className="text-[11px] font-semibold tabular-nums">
            {e.cvCount.toLocaleString('ja-JP')}
          </div>
          {e.cvRate !== null && (
            <div className="text-[10px] text-[var(--accent)] tabular-nums">
              {(e.cvRate * 100).toFixed(1)}%
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// メインビュー
// ─────────────────────────────────────────────
export function CampaignMonitoringView({ campaignId, campaign }: Props) {
  const { data: funnel, isLoading } = useCampaignFunnel(campaignId, campaign);
  const { data: broadcastCounts } = useLineNewsBroadcastCounts(campaignId);

  const { layers, orphans } = buildCampaignLayers(campaign);

  // 参加実数クイック編集ダイアログ
  const [editingEvent, setEditingEvent] = useState<CampaignElement | null>(null);
  const handleEditEvent = (el: CampaignElement) => setEditingEvent(el);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (layers.length === 0) {
    return (
      <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] p-8 text-center">
        <p className="text-sm text-[var(--text-muted)]">
          集計対象カードがありません。ボードでカードを追加してください。
        </p>
      </div>
    );
  }

  // 「fromCardId が layer i に属する → toCardId が layer i+1 に属する」エッジを抽出
  const edgesByLayer = new Map<number, EdgeStats[]>();
  if (funnel) {
    const layerOf = new Map<string, number>();
    layers.forEach((layer, idx) => {
      for (const el of layer) layerOf.set(el.id, idx);
    });
    for (const e of funnel.edges) {
      const fromLayer = layerOf.get(e.fromCardId);
      if (fromLayer === undefined) continue;
      const arr = edgesByLayer.get(fromLayer) ?? [];
      arr.push(e);
      edgesByLayer.set(fromLayer, arr);
    }
  }

  return (
    <div className="space-y-4">
      <div className="text-xs text-[var(--text-muted)] flex items-center gap-3">
        <span>計測イベント: {funnel?.totalEvents ?? 0} 件</span>
        {funnel?.truncated && (
          <span className="text-amber-500 flex items-center gap-1">
            <AlertTriangle className="size-3" />
            5000 件超のため一部切り捨て
          </span>
        )}
      </div>

      <div
        className="overflow-x-auto pb-3"
        style={{ scrollbarGutter: 'stable' }}
      >
        <div className="flex items-stretch gap-1 min-h-[260px]">
          {layers.map((layer, i) => (
            <div key={`layer-${i}`} className="flex items-stretch">
              <div className="flex flex-col gap-3 justify-center">
                {layer.map((el) => (
                  <StatsCard
                    key={el.id}
                    el={el}
                    stats={funnel?.cards.get(el.id)}
                    broadcastCount={
                      el.refId ? broadcastCounts?.get(el.refId) ?? null : null
                    }
                    onEditEvent={handleEditEvent}
                  />
                ))}
              </div>
              {i < layers.length - 1 && (
                <CvBadge edges={edgesByLayer.get(i) ?? []} />
              )}
            </div>
          ))}
        </div>
      </div>

      {orphans.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-[var(--text-muted)]">
            未接続カード（フローに含まれていません）
          </div>
          <div className="flex gap-3 flex-wrap">
            {orphans.map((el) => (
              <StatsCard
                key={el.id}
                el={el}
                stats={funnel?.cards.get(el.id)}
                broadcastCount={
                  el.refId ? broadcastCounts?.get(el.refId) ?? null : null
                }
                onEditEvent={handleEditEvent}
              />
            ))}
          </div>
        </div>
      )}

      <EventAttendanceQuickEditDialog
        open={editingEvent !== null}
        onOpenChange={(o) => {
          if (!o) setEditingEvent(null);
        }}
        eventId={editingEvent?.refId ?? null}
        eventLabel={editingEvent?.refLabel ?? null}
        campaignId={campaignId}
      />
    </div>
  );
}
